// Loaded first: provides `Array.prototype.at` on Safari/iOS < 15.4 before any
// app code (e.g. the call layout hooks) calls it. See ./libs/polyfills/arrayAt.
import './libs/polyfills/arrayAt';
import { Buffer } from 'buffer';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { initializeApp } from './libs/init/exec.init';
import { canonicalizeAuthTrailingSlash } from './shared/routerUtils/canonicalizeAuthTrailingSlash';
import { routes } from './routing/routes';
import { getAppBasename } from './hooks/useAppRouter/getAppBasename';
import {
  cleanupExpiredChunkReloadMarker,
  registerChunkPreloadErrorHandler,
  setChunkReloadActiveCallGuard,
} from './components/global/errorBoundary/chunkReload';
import { isInActiveCall } from './components/global/errorBoundary/isInActiveCall';

// Some dependencies (e.g. `isemail`) expect a global `Buffer`. The `buffer`
// alias in `vite.config.ts` provides the module; expose it globally before any
// app code that relies on it runs.
globalThis.Buffer ??= Buffer;

// Skip stale-chunk reload recovery while a call is live so a recoverable
// failure does not tear down the consultation; the retry + boundary fallback
// chain handles it instead. Registered before the preload handler below.
setChunkReloadActiveCallGuard(isInActiveCall);

// Recover from stale-deploy chunk failures (rotated asset hashes) via a
// one-time reload before they surface as uncaught errors. Registered first so
// the listener is active for any dynamic import during init or mount.
registerChunkPreloadErrorHandler();

// Retire recovery markers whose cooldown has elapsed so a later, unrelated
// chunk failure gets its own recovery attempt (and the URL stays clean).
// Markers still inside the cooldown window are kept — they are the guard that
// stops a reload loop when the previous reload did not fix the failure.
cleanupExpiredChunkReloadMarker();

/**
 * Client entry for the Vite SPA: normalize auth URLs, run app init, then mount
 * React Router 7 synchronously (pure CSR).
 */
canonicalizeAuthTrailingSlash();

await initializeApp();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element');
}

const router = createBrowserRouter(routes, { basename: getAppBasename() });

createRoot(rootElement).render(<RouterProvider router={router} />);
